import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { estimatesAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Card, CardContent } from '../../components/ui/Card';
import { FormLabel } from '../../components/ui/FormLabel';
import { cn } from '../../lib/utils';
import { format } from 'date-fns';
import { getEffectiveEstimateStatus } from '../../lib/estimateStatus';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  Copy,
  Filter,
  MessageSquare,
  Printer,
  Receipt,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react';

const Estimates = () => {
  const navigate = useNavigate();

  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewFilter, setViewFilter] = useState('active');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [clientFilter, setClientFilter] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const dropdownRefs = useRef({});
  const dropdownMenuRef = useRef(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [reissuingId, setReissuingId] = useState(null);

  useEffect(() => {
    fetchEstimates();
  }, [pagination.page, search, statusFilter, viewFilter, clientFilter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const activeRef = openDropdownId ? dropdownRefs.current[openDropdownId] : null;
      const clickedInsideMenu = dropdownMenuRef.current?.contains(e.target);
      if (activeRef && !activeRef.contains(e.target) && !clickedInsideMenu) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  useEffect(() => {
    if (!openDropdownId) {
      setDropdownPosition(null);
      return;
    }
    const el = dropdownRefs.current[openDropdownId];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 4, left: rect.left });

    const closeOnScroll = () => setOpenDropdownId(null);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnScroll);
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', closeOnScroll);
    };
  }, [openDropdownId]);

  const fetchEstimates = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await estimatesAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search,
        status: statusFilter || undefined,
        view: viewFilter,
        clientId: clientFilter || undefined,
      });

      setEstimates(response.estimates);
      setPagination((prev) => ({
        ...prev,
        ...response.pagination,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load estimates');
      console.error('Error fetching estimates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEstimate = async (estimateId) => {
    if (!confirm('Are you sure you want to delete this estimate?')) return;

    try {
      await estimatesAPI.delete(estimateId);
      fetchEstimates();
    } catch (err) {
      alert(err.message || 'Failed to delete estimate');
      console.error('Delete error:', err);
    }
  };

  const handleArchiveEstimate = async (estimateId, isArchived) => {
    try {
      await estimatesAPI.archive(estimateId, !isArchived);
      fetchEstimates();
    } catch (err) {
      alert(err.message || 'Failed to update estimate');
      console.error('Archive error:', err);
    }
  };

  const getClientDisplayName = (client) => {
    if (client.clientType === 'company') {
      return client.companyName || 'Unnamed Company';
    }
    return `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed Individual';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatEstimateDate = (dateStr) => {
    if (!dateStr) return '—';
    return format(new Date(dateStr), 'dd/MM/yy');
  };

  const getStatusTriggerClass = (estimate) => {
    if (estimate.isDeleted) {
      return 'bg-[#E15230] text-white border-[#E15230] hover:opacity-95';
    }
    const s = getEffectiveEstimateStatus({
      status: estimate.status,
      expiryDate: estimate.expiryDate,
    });
    if (s === 'converted') {
      return 'bg-slate-600 text-white border-transparent hover:opacity-95';
    }
    if (s === 'accepted') {
      return 'bg-[var(--ds-status-success)] text-white border-transparent hover:opacity-95';
    }
    if (s === 'rejected') {
      return 'bg-slate-500 text-white border-transparent hover:opacity-95';
    }
    if (s === 'queried') {
      return 'bg-sky-600 text-white border-transparent hover:opacity-95';
    }
    if (s === 'expired') {
      return 'bg-[var(--ds-status-danger)] text-white border-transparent hover:opacity-95';
    }
    if (s === 'sent') {
      return 'bg-amber-600 text-white border-transparent hover:opacity-95';
    }
    return 'bg-[var(--ds-accent-primary)] text-white border-[var(--ds-accent-primary)] hover:opacity-95';
  };

  const getStatusTriggerLabel = (estimate) => {
    if (estimate.isDeleted) return 'DELETED';
    const display = getEffectiveEstimateStatus({
      status: estimate.status,
      expiryDate: estimate.expiryDate,
    });
    const base = (display || '').replace(/-/g, ' ');
    if (estimate.isArchived) return `${base.toUpperCase()} · ARCH`;
    return base.toUpperCase();
  };

  const handleFilterOnClient = (clientId) => {
    setOpenDropdownId(null);
    if (!clientId) return;
    setClientFilter(clientId);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleReissue = async (estimate) => {
    setOpenDropdownId(null);
    const ok = window.confirm(
      `Re-issue estimate ${estimate.estimateNum} as Sent? Any stored client link response on this estimate will be cleared (recent activity is kept).`
    );
    if (!ok) return;
    try {
      setReissuingId(estimate.id);
      await estimatesAPI.reissue(estimate.id);
      await fetchEstimates();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to re-issue estimate');
    } finally {
      setReissuingId(null);
    }
  };

  const handleDuplicate = async (estimate) => {
    setOpenDropdownId(null);
    try {
      setDuplicatingId(estimate.id);
      const data = await estimatesAPI.duplicate(estimate.id);
      const newId = data?.estimate?.id;
      if (!newId) throw new Error('Duplicate failed');
      await fetchEstimates();
      navigate(`/tenant/estimates/${newId}`);
    } catch (err) {
      alert(err.message || 'Failed to duplicate estimate');
      console.error(err);
    } finally {
      setDuplicatingId(null);
    }
  };

  const handlePrintEstimate = async (estimate) => {
    setOpenDropdownId(null);
    try {
      const html = await estimatesAPI.getHtml(estimate.id);
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(typeof html === 'string' ? html : '');
      w.document.close();
      setTimeout(() => {
        w.focus();
        w.print();
      }, 300);
    } catch (err) {
      alert(err.message || 'Failed to open print view');
    }
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Estimates</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage your estimates and quotes
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button variant="outline" size="sm" onClick={() => navigate('/tenant/dashboard')}>
                ← Back to Dashboard
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/tenant/estimates/new')}>
                + New Estimate
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <FormLabel>Search</FormLabel>
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  placeholder="Search by estimate number, reference, or client..."
                  className="h-8"
                />
              </div>
              <div>
                <FormLabel>Status</FormLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  className="h-8"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="queried">Queried</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="converted">Converted</option>
                </Select>
              </div>
              <div>
                <FormLabel>View</FormLabel>
                <Select
                  value={viewFilter}
                  onChange={(e) => {
                    setViewFilter(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  className="h-8"
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                  <option value="deleted">Deleted</option>
                </Select>
              </div>
              {clientFilter && (
                <div className="md:col-span-4 flex items-center gap-2">
                  <span className="text-micro text-[var(--ds-text-muted)]">Client filter active</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setClientFilter('');
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mb-4 p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-xs text-gray-500">Loading estimates...</p>
          </div>
        ) : estimates.length === 0 ? (
          <Card className="text-center py-12">
            <p className="text-xs text-[var(--ds-text-muted)]">No estimates found</p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/tenant/estimates/new')}
              className="mt-4"
            >
              Create Your First Estimate
            </Button>
          </Card>
        ) : (
          <>
            <Card className="overflow-hidden">
              <ul className="divide-y divide-[var(--ds-border-divider)]">
                <li className="px-4 py-2 sm:px-6 bg-[var(--ds-control-field-bg)] border-b border-[var(--ds-border-divider)]">
                  <div className="flex items-center gap-4">
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[100px]">Estimate</div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] flex-1 min-w-0">Client</div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[100px] shrink-0">
                      Reference
                    </div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[88px] shrink-0">
                      Date Issued
                    </div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[88px] shrink-0">
                      Expiry Date
                    </div>
                    <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[80px] text-right shrink-0">
                      Amount
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-xs font-medium text-[var(--ds-text-secondary)] min-w-[104px]">Status</div>
                      <div className="text-xs font-medium text-[var(--ds-text-secondary)]">View</div>
                    </div>
                  </div>
                </li>
                {estimates.map((estimate) => (
                  <li key={estimate.id}>
                    <div className="px-4 py-3 sm:px-6 hover:bg-[var(--ds-control-field-bg)] transition-colors duration-180">
                      <div className="flex items-center gap-4">
                        {estimate.isDeleted ? (
                          <div className="text-xs font-medium text-[var(--ds-text-muted)] min-w-[100px] truncate">
                            {estimate.estimateNum}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate(`/tenant/estimates/${estimate.id}`)}
                            className="min-w-[100px] max-w-[140px] truncate text-left text-xs font-medium text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 inline-flex items-center gap-1"
                          >
                            {estimate.clientPortalResponse ? (
                              <MessageSquare
                                className="h-3.5 w-3.5 shrink-0 text-sky-600 dark:text-sky-400"
                                aria-hidden
                              />
                            ) : null}
                            <span className="truncate">{estimate.estimateNum}</span>
                          </button>
                        )}

                        <div className="text-xs text-[var(--ds-text-muted)] flex-1 min-w-0 truncate">
                          {getClientDisplayName(estimate.client)}
                        </div>

                        <div className="text-xs text-[var(--ds-text-muted)] min-w-[100px] shrink-0 truncate">
                          {estimate.reference || '—'}
                        </div>

                        <div className="text-xs text-[var(--ds-text-muted)] min-w-[88px] shrink-0 tabular-nums">
                          {formatEstimateDate(estimate.dateIssued)}
                        </div>

                        <div className="text-xs text-[var(--ds-text-muted)] min-w-[88px] shrink-0 tabular-nums">
                          {formatEstimateDate(estimate.expiryDate)}
                        </div>

                        <div className="text-xs font-semibold text-[var(--ds-text-primary)] tabular-nums text-right min-w-[80px] shrink-0">
                          {formatCurrency(estimate.total)}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <div
                            className="relative inline-block shrink-0"
                            ref={(el) => {
                              dropdownRefs.current[estimate.id] = el;
                            }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setOpenDropdownId(openDropdownId === estimate.id ? null : estimate.id)
                              }
                              className={cn(
                                'inline-flex h-6 min-w-[104px] max-w-[152px] items-center justify-between gap-0.5 rounded-[4px] border px-1.5 py-0 text-xs font-semibold uppercase tracking-wide leading-none shadow-none transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ds-surface-panel)]',
                                getStatusTriggerClass(estimate)
                              )}
                              aria-expanded={openDropdownId === estimate.id}
                              aria-haspopup="menu"
                            >
                              <span className="truncate">{getStatusTriggerLabel(estimate)}</span>
                              <ChevronDown className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                            </button>
                            {openDropdownId === estimate.id &&
                              dropdownPosition &&
                              createPortal(
                                <div
                                  ref={dropdownMenuRef}
                                  className="fixed z-[9999] min-w-[200px] max-w-[260px] rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] py-1 shadow-lg"
                                  role="menu"
                                  style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleDuplicate(estimate)}
                                    disabled={duplicatingId === estimate.id}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)] disabled:cursor-not-allowed disabled:opacity-50"
                                    role="menuitem"
                                  >
                                    <Copy className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                    {duplicatingId === estimate.id ? 'Copying…' : 'Make a copy'}
                                  </button>
                                  {!estimate.isDeleted &&
                                    (estimate.status === 'draft' || estimate.status === 'queried') && (
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        setOpenDropdownId(null);
                                        if (!confirm('Mark this estimate as sent?')) return;
                                        try {
                                          await estimatesAPI.send(estimate.id);
                                          fetchEstimates();
                                        } catch (err) {
                                          alert(err.message || 'Failed to update');
                                        }
                                      }}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                      role="menuitem"
                                    >
                                      <Send className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      Mark as sent
                                    </button>
                                  )}
                                  {!estimate.isDeleted &&
                                    ['rejected', 'queried', 'accepted'].includes(estimate.status) && (
                                      <button
                                        type="button"
                                        onClick={() => handleReissue(estimate)}
                                        disabled={reissuingId === estimate.id}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)] disabled:cursor-not-allowed disabled:opacity-50"
                                        role="menuitem"
                                      >
                                        <RefreshCw
                                          className={`h-4 w-4 shrink-0 text-[var(--ds-text-muted)] ${reissuingId === estimate.id ? 'animate-spin' : ''}`}
                                        />
                                        {reissuingId === estimate.id ? 'Re-issuing…' : 'Re-issue as sent'}
                                      </button>
                                    )}
                                  {!estimate.isDeleted &&
                                    ['sent', 'accepted'].includes(estimate.status) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenDropdownId(null);
                                          navigate(`/tenant/estimates/${estimate.id}/convert`);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                        role="menuitem"
                                      >
                                        <Receipt className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                        Convert to invoice
                                      </button>
                                    )}
                                  {!estimate.isDeleted && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        navigate(`/tenant/estimates/${estimate.id}/send`);
                                      }}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                      role="menuitem"
                                    >
                                      <Send className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      Send estimate
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handlePrintEstimate(estimate)}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                    role="menuitem"
                                  >
                                    <Printer className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                    Print
                                  </button>
                                  {estimate.client?.id && (
                                    <button
                                      type="button"
                                      onClick={() => handleFilterOnClient(estimate.client.id)}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                      role="menuitem"
                                    >
                                      <Filter className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      Filter on client
                                    </button>
                                  )}
                                  {!estimate.isDeleted && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        handleArchiveEstimate(estimate.id, estimate.isArchived);
                                      }}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                                      role="menuitem"
                                    >
                                      {estimate.isArchived ? (
                                        <ArchiveRestore className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      ) : (
                                        <Archive className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" />
                                      )}
                                      {estimate.isArchived ? 'Unarchive' : 'Archive'}
                                    </button>
                                  )}
                                  {!estimate.isDeleted && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        handleDeleteEstimate(estimate.id);
                                      }}
                                      className="mx-1 mt-0.5 flex w-[calc(100%-0.5rem)] items-center gap-2 rounded-[var(--ds-radius-sm)] bg-[var(--ds-status-danger)] px-3 py-2 text-left text-xs font-medium text-white hover:opacity-95"
                                      role="menuitem"
                                    >
                                      <Trash2 className="h-4 w-4 shrink-0 text-white" />
                                      Delete
                                    </button>
                                  )}
                                </div>,
                                document.body
                              )}
                          </div>
                          <button
                            type="button"
                            onClick={() => navigate(`/tenant/estimates/${estimate.id}/view`)}
                            className="text-xs font-medium text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)] hover:underline shrink-0"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            {pagination.totalPages > 1 && (
              <Card className="mt-6">
                <CardContent className="px-4 py-3 sm:px-6">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs text-[var(--ds-text-secondary)]">
                        Showing{' '}
                        <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(pagination.page * pagination.limit, pagination.total)}
                        </span>{' '}
                        of <span className="font-medium">{pagination.total}</span> results
                      </p>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-[var(--ds-radius-md)] shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                          disabled={pagination.page === 1}
                          className="rounded-l-[var(--ds-radius-md)] rounded-r-none"
                        >
                          Previous
                        </Button>
                        {[...Array(pagination.totalPages)].map((_, i) => (
                          <Button
                            key={i + 1}
                            variant={pagination.page === i + 1 ? 'primary' : 'outline'}
                            size="sm"
                            onClick={() => setPagination((prev) => ({ ...prev, page: i + 1 }))}
                            className="rounded-none"
                          >
                            {i + 1}
                          </Button>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                          disabled={pagination.page >= pagination.totalPages}
                          className="rounded-r-[var(--ds-radius-md)] rounded-l-none"
                        >
                          Next
                        </Button>
                      </nav>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Estimates;
